import { useState, useCallback } from 'react';
import { useProfile } from '@/hooks/useProfile';

/**
 * Manages the enhanced coaching opt-in flow.
 *
 * Returns whether the opt-in modal should be shown and helpers
 * to trigger it. The coach panel (PR 4) calls `guardCoachAction()`
 * before the first coaching call — if the user hasn't been prompted
 * yet, the modal opens instead of proceeding.
 */
export function useCoachOptIn() {
  const { profile } = useProfile();
  const [modalOpen, setModalOpen] = useState(false);

  // Profile fields — cast because enhanced_coaching_prompted
  // isn't in generated types until the migration is applied and
  // types are regenerated (follow-up issue #323).
  const prompted = (profile as Record<string, unknown> | null)?.enhanced_coaching_prompted === true;
  const enabled = (profile as Record<string, unknown> | null)?.enhanced_coaching_enabled === true;

  /**
   * Call before initiating a coaching action.
   * Returns `true` if the action can proceed, `false` if the
   * opt-in modal was shown instead.
   */
  const guardCoachAction = useCallback((): boolean => {
    if (!profile) return true; // Profile loading — allow action, don't block
    if (!prompted) {
      setModalOpen(true);
      return false;
    }
    return true;
  }, [profile, prompted]);

  return {
    /** Whether the opt-in modal is currently open */
    modalOpen,
    /** Control modal visibility (passed to AlertDialog onOpenChange) */
    setModalOpen,
    /** Whether the user has already been prompted */
    prompted,
    /** Whether enhanced coaching is enabled */
    enabled,
    /** Gate a coaching action — returns false and opens modal if not yet prompted */
    guardCoachAction,
  };
}
